import React, { useState, useEffect, useCallback, useRef } from "react";
import API_BASE from "../apiConfig";
import { useTheme, getT } from "../ThemeContext";
import { useSort } from "../hooks/useSort";
import { useDateTimeFormat } from "../utils/formatters";
import ExcelDownloadButton from "../components/ExcelDownloadButton";
import { PrimaryButton, GhostButton } from "../components/ThemedButton";

async function api(path, options = {}) {
  const res = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers: { "Content-Type": "application/json", ...(options.headers || {}) },
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok || data.success === false) {
    throw new Error(data.message || `Request failed (${res.status})`);
  }
  return data;
}

const TXN_TYPES = ["adjustment", "correction", "damage", "loss", "purchase"];

export default function POSInventory({ currentUser }) {
  const { isDark } = useTheme();
  const T = getT(isDark);
  const { fmtDateTime } = useDateTimeFormat();

  const canAdjust = ["Staff", "Admin", "Super Admin"].includes(currentUser?.user_role);

  const [locations, setLocations] = useState([]);
  const [locationId, setLocationId] = useState(null);
  const [tab, setTab] = useState("stock"); // stock | history

  const [inventory, setInventory] = useState([]);
  const [lowStockOnly, setLowStockOnly] = useState(false);
  const [stockSearch, setStockSearch] = useState("");
  const [stockLoading, setStockLoading] = useState(false);

  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  const [error, setError] = useState("");

  const [adjustRow, setAdjustRow] = useState(null); // inventory row being adjusted
  const [adjustDelta, setAdjustDelta] = useState("");
  const [adjustType, setAdjustType] = useState("adjustment");
  const [adjustNotes, setAdjustNotes] = useState("");
  const [adjustConfirming, setAdjustConfirming] = useState(false);
  const [adjustBusy, setAdjustBusy] = useState(false);

  // Load Stock — receive initial/new stock for any catalog product, whether
  // or not it already has an inventory row at this location.
  const [loadOpen, setLoadOpen] = useState(false);
  const [catalogProducts, setCatalogProducts] = useState([]);
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [loadProductSearch, setLoadProductSearch] = useState("");
  const [loadProductId, setLoadProductId] = useState("");
  const [loadVariants, setLoadVariants] = useState([]);
  const [loadVariantId, setLoadVariantId] = useState("");
  const [loadQty, setLoadQty] = useState("");
  const [loadNotes, setLoadNotes] = useState("");
  const [loadBusy, setLoadBusy] = useState(false);

  // Bulk import from Excel template
  const importInputRef = useRef(null);
  const [importBusy, setImportBusy] = useState(false);
  const [importResults, setImportResults] = useState(null);

  useEffect(() => {
    api("/pos/locations/")
      .then((d) => {
        setLocations(d.locations);
        if (d.locations.length) setLocationId(d.locations[0].location_id);
      })
      .catch((e) => setError(e.message));
  }, []);

  const loadInventory = useCallback(() => {
    if (!locationId) return;
    setStockLoading(true);
    api(`/pos/inventory/?location_id=${locationId}&low_stock_only=${lowStockOnly}`)
      .then((d) => setInventory(d.inventory))
      .catch((e) => setError(e.message))
      .finally(() => setStockLoading(false));
  }, [locationId, lowStockOnly]);

  const loadHistory = useCallback(() => {
    if (!locationId) return;
    setHistoryLoading(true);
    api(`/pos/inventory/history?location_id=${locationId}&limit=200`)
      .then((d) => setHistory(d.transactions))
      .catch((e) => setError(e.message))
      .finally(() => setHistoryLoading(false));
  }, [locationId]);

  useEffect(() => { loadInventory(); }, [loadInventory]);
  useEffect(() => { if (tab === "history") loadHistory(); }, [tab, loadHistory]);

  const filteredInventory = inventory.filter((r) => {
    if (!stockSearch) return true;
    const q = stockSearch.toLowerCase();
    return (
      (r.product_name || "").toLowerCase().includes(q) ||
      (r.sku || "").toLowerCase().includes(q) ||
      (r.variant_name || "").toLowerCase().includes(q)
    );
  });

  // Hooks must be called unconditionally, before any early return.
  const stockSort = useSort(filteredInventory, "product_name");
  const historySort = useSort(history, "txn_id", "desc");

  function openAdjust(row) {
    setAdjustRow(row);
    setAdjustDelta("");
    setAdjustType("adjustment");
    setAdjustNotes("");
    setAdjustConfirming(false);
    setError("");
  }

  function openLoadStock() {
    setLoadOpen(true);
    setLoadProductSearch("");
    setLoadProductId("");
    setLoadVariants([]);
    setLoadVariantId("");
    setLoadQty("");
    setLoadNotes("");
    setError("");
    if (!catalogProducts.length) {
      setCatalogLoading(true);
      api("/pos/catalog/products?active_only=true&page_size=500")
        .then((d) => setCatalogProducts(d.products))
        .catch((e) => setError(e.message))
        .finally(() => setCatalogLoading(false));
    }
  }

  function selectLoadProduct(productId) {
    setLoadProductId(productId);
    setLoadVariantId("");
    setLoadVariants([]);
    if (!productId) return;
    api(`/pos/catalog/products/${productId}`)
      .then((d) => setLoadVariants(d.product.variants || []))
      .catch((e) => setError(e.message));
  }

  async function submitLoadStock() {
    const qty = parseInt(loadQty, 10);
    if (!loadProductId || !qty || qty <= 0) {
      setError("Pick a product and enter a positive quantity to receive.");
      return;
    }
    setLoadBusy(true); setError("");
    try {
      await api("/pos/inventory/adjust", {
        method: "POST",
        body: JSON.stringify({
          location_id: locationId,
          product_id: Number(loadProductId),
          variant_id: loadVariantId ? Number(loadVariantId) : null,
          quantity_delta: qty,
          txn_type: "purchase",
          notes: loadNotes || null,
        }),
      });
      setLoadOpen(false);
      loadInventory();
      if (tab === "history") loadHistory();
    } catch (e) {
      setError(e.message);
    } finally {
      setLoadBusy(false);
    }
  }

  async function handleImportFile(e) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !locationId) return;
    setImportBusy(true); setError(""); setImportResults(null);
    try {
      const form = new FormData();
      form.append("file", file);
      const res = await fetch(`${API_BASE}/pos/inventory/import?location_id=${locationId}`, {
        method: "POST",
        body: form,
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data.success === false) {
        throw new Error(data.message || `Import failed (${res.status})`);
      }
      setImportResults(data.results || []);
      loadInventory();
      if (tab === "history") loadHistory();
    } catch (err) {
      setError(err.message);
    } finally {
      setImportBusy(false);
    }
  }

  const filteredCatalogProducts = catalogProducts.filter((p) => {
    if (!loadProductSearch) return true;
    const q = loadProductSearch.toLowerCase();
    return (p.name || "").toLowerCase().includes(q) || (p.sku || "").toLowerCase().includes(q);
  });

  async function submitAdjust() {
    const delta = parseInt(adjustDelta, 10);
    if (!adjustRow || !delta) {
      setError("Enter a non-zero quantity change.");
      return;
    }
    setAdjustBusy(true); setError("");
    try {
      await api("/pos/inventory/adjust", {
        method: "POST",
        body: JSON.stringify({
          location_id: locationId,
          product_id: adjustRow.product_id,
          variant_id: adjustRow.variant_id || null,
          quantity_delta: delta,
          txn_type: adjustType,
          notes: adjustNotes || null,
        }),
      });
      setAdjustRow(null);
      loadInventory();
      if (tab === "history") loadHistory();
    } catch (e) {
      setError(e.message);
    } finally {
      setAdjustBusy(false);
    }
  }

  const card = { background: T.glass, border: `1px solid ${T.glassBdr}`, borderRadius: 14, padding: 16 };
  const input = { padding: "8px 10px", borderRadius: 8, border: `1px solid ${T.inputBdr}`, background: T.inputBg, color: T.inputText, fontSize: 13 };
  const th = { textAlign: "left", padding: "8px 10px", fontSize: 12, color: T.textSub, borderBottom: `1px solid ${T.divider}` };
  const td = { padding: "8px 10px", fontSize: 13, borderBottom: `1px solid ${T.divider}` };
  const tabBtn = (active) => ({
    background: active ? T.tabActiveBg : "transparent",
    color: active ? T.tabActiveClr : T.textSub,
    border: `1px solid ${active ? "transparent" : T.glassBdr}`,
    borderRadius: 8, padding: "8px 16px", fontWeight: 700, fontSize: 13, cursor: "pointer",
  });

  return (
    <div style={{ color: T.textPrimary, display: "flex", flexDirection: "column", gap: 16 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
        <h2 style={{ margin: 0 }}>Inventory &amp; Stock</h2>
        <div style={{ display: "flex", gap: 8, marginLeft: "auto", flexWrap: "wrap", alignItems: "center" }}>
          <ExcelDownloadButton
            endpoint="/pos/inventory/export"
            params={{ location_id: locationId }}
            filename="inventory_export.xlsx"
            label="Export Inventory"
            disabled={!locationId}
          />
          {canAdjust && (
            <>
              <ExcelDownloadButton
                endpoint="/pos/inventory/template"
                params={{ location_id: locationId }}
                filename="inventory_load_template.xlsx"
                label="Download Template"
                disabled={!locationId}
              />
              <GhostButton size="sm" disabled={!locationId || importBusy} onClick={() => importInputRef.current?.click()}>
                {importBusy ? "Importing…" : "Import from Excel"}
              </GhostButton>
              <input ref={importInputRef} type="file" accept=".xlsx" style={{ display: "none" }} onChange={handleImportFile} />
              <PrimaryButton onClick={openLoadStock}>+ Load Stock</PrimaryButton>
            </>
          )}
        </div>
        <select value={locationId || ""} onChange={(e) => setLocationId(Number(e.target.value))} style={input}>
          {locations.map((l) => <option key={l.location_id} value={l.location_id}>{l.name}</option>)}
        </select>
      </div>

      {error && <div style={{ color: T.red }}>{error}</div>}

      <div style={{ display: "flex", gap: 8 }}>
        <button style={tabBtn(tab === "stock")} onClick={() => setTab("stock")}>Current Stock</button>
        <button style={tabBtn(tab === "history")} onClick={() => setTab("history")}>Movement History</button>
      </div>

      {tab === "stock" && (
        <div style={card}>
          <div style={{ display: "flex", gap: 10, marginBottom: 12, flexWrap: "wrap", alignItems: "center" }}>
            <input
              placeholder="Search product, SKU, variant…"
              value={stockSearch}
              onChange={(e) => setStockSearch(e.target.value)}
              style={{ ...input, flex: 1, minWidth: 220 }}
            />
            <label style={{ fontSize: 12, color: T.textSub, display: "flex", alignItems: "center", gap: 6 }}>
              <input type="checkbox" checked={lowStockOnly} onChange={(e) => setLowStockOnly(e.target.checked)} />
              Low stock only
            </label>
            <GhostButton size="sm" onClick={loadInventory}>{stockLoading ? "Refreshing…" : "Refresh"}</GhostButton>
          </div>

          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  <th style={{ ...th, ...stockSort.thSort("product_name", T) }} onClick={() => stockSort.toggleSort("product_name")}>
                    Product{stockSort.sortArrow("product_name")}
                  </th>
                  <th style={{ ...th, ...stockSort.thSort("variant_name", T) }} onClick={() => stockSort.toggleSort("variant_name")}>
                    Variant{stockSort.sortArrow("variant_name")}
                  </th>
                  <th style={{ ...th, ...stockSort.thSort("sku", T) }} onClick={() => stockSort.toggleSort("sku")}>
                    SKU{stockSort.sortArrow("sku")}
                  </th>
                  <th style={{ ...th, ...stockSort.thSort("on_hand", T) }} onClick={() => stockSort.toggleSort("on_hand")}>
                    On Hand{stockSort.sortArrow("on_hand")}
                  </th>
                  <th style={{ ...th, ...stockSort.thSort("reserved", T) }} onClick={() => stockSort.toggleSort("reserved")}>
                    Reserved{stockSort.sortArrow("reserved")}
                  </th>
                  <th style={{ ...th, ...stockSort.thSort("available", T) }} onClick={() => stockSort.toggleSort("available")}>
                    Available{stockSort.sortArrow("available")}
                  </th>
                  {canAdjust && <th style={th}>Actions</th>}
                </tr>
              </thead>
              <tbody>
                {stockSort.sorted.map((r) => {
                  const reorderLevel = r.reorder_level ?? r.product_reorder_level ?? null;
                  const low = reorderLevel !== null && r.available <= reorderLevel;
                  return (
                    <tr key={`${r.product_id}-${r.variant_id || 0}`}>
                      <td style={td}>{r.product_name}</td>
                      <td style={{ ...td, color: T.textSub }}>{r.variant_name || "—"}</td>
                      <td style={{ ...td, color: T.textSub }}>{r.sku}</td>
                      <td style={td}>{r.on_hand}</td>
                      <td style={{ ...td, color: T.textSub }}>{r.reserved}</td>
                      <td style={{ ...td, fontWeight: 700, color: low ? T.red : T.textPrimary }}>
                        {r.available}{low && " ⚠ Low"}
                      </td>
                      {canAdjust && (
                        <td style={td}>
                          <GhostButton size="sm" onClick={() => openAdjust(r)}>Adjust</GhostButton>
                        </td>
                      )}
                    </tr>
                  );
                })}
                {!stockSort.sorted.length && (
                  <tr><td style={td} colSpan={canAdjust ? 7 : 6}>
                    <span style={{ color: T.textMuted }}>{stockLoading ? "Loading…" : "No inventory rows for this location."}</span>
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === "history" && (
        <div style={card}>
          <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 12 }}>
            <GhostButton size="sm" onClick={loadHistory}>{historyLoading ? "Refreshing…" : "Refresh"}</GhostButton>
          </div>
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  <th style={{ ...th, ...historySort.thSort("created_at", T) }} onClick={() => historySort.toggleSort("created_at")}>
                    Date{historySort.sortArrow("created_at")}
                  </th>
                  <th style={{ ...th, ...historySort.thSort("product_id", T) }} onClick={() => historySort.toggleSort("product_id")}>
                    Product ID{historySort.sortArrow("product_id")}
                  </th>
                  <th style={{ ...th, ...historySort.thSort("txn_type", T) }} onClick={() => historySort.toggleSort("txn_type")}>
                    Type{historySort.sortArrow("txn_type")}
                  </th>
                  <th style={{ ...th, ...historySort.thSort("quantity_delta", T) }} onClick={() => historySort.toggleSort("quantity_delta")}>
                    Qty Δ{historySort.sortArrow("quantity_delta")}
                  </th>
                  <th style={{ ...th, ...historySort.thSort("reference_type", T) }} onClick={() => historySort.toggleSort("reference_type")}>
                    Reference{historySort.sortArrow("reference_type")}
                  </th>
                  <th style={{ ...th, ...historySort.thSort("notes", T) }} onClick={() => historySort.toggleSort("notes")}>
                    Notes{historySort.sortArrow("notes")}
                  </th>
                  <th style={{ ...th, ...historySort.thSort("created_by", T) }} onClick={() => historySort.toggleSort("created_by")}>
                    By{historySort.sortArrow("created_by")}
                  </th>
                </tr>
              </thead>
              <tbody>
                {historySort.sorted.map((h) => (
                  <tr key={h.txn_id}>
                    <td style={td}>{fmtDateTime(h.created_at)}</td>
                    <td style={{ ...td, color: T.textSub }}>
                      {h.product_id}{h.variant_id ? ` / var ${h.variant_id}` : ""}
                    </td>
                    <td style={td}>{h.txn_type}</td>
                    <td style={{ ...td, fontWeight: 700, color: h.quantity_delta < 0 ? T.red : T.green || T.textPrimary }}>
                      {h.quantity_delta > 0 ? `+${h.quantity_delta}` : h.quantity_delta}
                    </td>
                    <td style={{ ...td, color: T.textSub }}>
                      {h.reference_type}{h.reference_id ? ` #${h.reference_id}` : ""}
                    </td>
                    <td style={{ ...td, color: T.textSub }}>{h.notes || "—"}</td>
                    <td style={{ ...td, color: T.textSub }}>{h.created_by ?? "—"}</td>
                  </tr>
                ))}
                {!historySort.sorted.length && (
                  <tr><td style={td} colSpan={7}>
                    <span style={{ color: T.textMuted }}>{historyLoading ? "Loading…" : "No movements recorded for this location."}</span>
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {importResults && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 50 }}>
          <div style={{ ...card, width: 480, maxHeight: "80vh", overflowY: "auto", background: isDark ? "#0f2436" : "#fff" }}>
            <h3 style={{ marginTop: 0 }}>Import Results</h3>
            <div style={{ fontSize: 13, color: T.textSub, marginBottom: 12 }}>
              {importResults.filter((r) => r.Status === "Loaded").length} of {importResults.length} rows loaded.
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 14 }}>
              {importResults.map((r, i) => (
                <div key={i} style={{ fontSize: 12, padding: "6px 8px", borderRadius: 6, background: T.glass, color: r.Status === "Loaded" ? T.textPrimary : T.red }}>
                  {r.Product || "(row)"} — {r.Status}{r["Quantity Received"] != null ? ` (+${r["Quantity Received"]})` : ""}
                </div>
              ))}
              {!importResults.length && <div style={{ fontSize: 12, color: T.textMuted }}>No rows with a quantity to receive were found.</div>}
            </div>
            <PrimaryButton style={{ width: "100%" }} onClick={() => setImportResults(null)}>Close</PrimaryButton>
          </div>
        </div>
      )}

      {loadOpen && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 50 }}>
          <div style={{ ...card, width: 420, background: isDark ? "#0f2436" : "#fff" }}>
            <h3 style={{ marginTop: 0 }}>Load Stock</h3>
            <div style={{ fontSize: 13, color: T.textSub, marginBottom: 12 }}>
              Receive new or initial stock for a product at this location.
            </div>

            <label style={{ fontSize: 12, color: T.textSub }}>Product</label>
            <input
              placeholder="Search product or SKU…"
              value={loadProductSearch}
              onChange={(e) => setLoadProductSearch(e.target.value)}
              style={{ ...input, width: "100%", marginTop: 4, marginBottom: 6, boxSizing: "border-box" }}
            />
            <select
              value={loadProductId}
              onChange={(e) => selectLoadProduct(e.target.value)}
              style={{ ...input, width: "100%", marginBottom: 10, boxSizing: "border-box" }}
            >
              <option value="">{catalogLoading ? "Loading products…" : "Select a product…"}</option>
              {filteredCatalogProducts.map((p) => (
                <option key={p.product_id} value={p.product_id}>{p.name}{p.sku ? ` (${p.sku})` : ""}</option>
              ))}
            </select>

            {loadProductId && loadVariants.length > 0 && (
              <>
                <label style={{ fontSize: 12, color: T.textSub }}>Variant</label>
                <select
                  value={loadVariantId}
                  onChange={(e) => setLoadVariantId(e.target.value)}
                  style={{ ...input, width: "100%", marginTop: 4, marginBottom: 10, boxSizing: "border-box" }}
                >
                  <option value="">No variant (base product)</option>
                  {loadVariants.map((v) => (
                    <option key={v.variant_id} value={v.variant_id}>{v.variant_name}</option>
                  ))}
                </select>
              </>
            )}

            <label style={{ fontSize: 12, color: T.textSub }}>Quantity received</label>
            <input
              type="number"
              min="1"
              value={loadQty}
              onChange={(e) => setLoadQty(e.target.value)}
              placeholder="e.g. 50"
              style={{ ...input, width: "100%", marginTop: 4, marginBottom: 10, boxSizing: "border-box" }}
            />

            <label style={{ fontSize: 12, color: T.textSub }}>Notes (optional)</label>
            <textarea
              value={loadNotes}
              onChange={(e) => setLoadNotes(e.target.value)}
              rows={2}
              placeholder="e.g. PO #1234, supplier delivery"
              style={{ ...input, width: "100%", marginTop: 4, marginBottom: 14, boxSizing: "border-box", resize: "vertical" }}
            />

            {error && <div style={{ color: T.red, marginBottom: 10, fontSize: 13 }}>{error}</div>}
            <div style={{ display: "flex", gap: 8 }}>
              <GhostButton style={{ flex: 1 }} onClick={() => setLoadOpen(false)} disabled={loadBusy}>Cancel</GhostButton>
              <PrimaryButton style={{ flex: 1 }} onClick={submitLoadStock} disabled={loadBusy}>
                {loadBusy ? "Saving…" : "Receive Stock"}
              </PrimaryButton>
            </div>
          </div>
        </div>
      )}

      {adjustRow && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 50 }}>
          <div style={{ ...card, width: 400, background: isDark ? "#0f2436" : "#fff" }}>
            <h3 style={{ marginTop: 0 }}>Adjust Stock</h3>
            <div style={{ fontSize: 13, color: T.textSub, marginBottom: 12 }}>
              {adjustRow.product_name}{adjustRow.variant_name ? ` — ${adjustRow.variant_name}` : ""} · Current on hand: <strong>{adjustRow.on_hand}</strong>
            </div>

            {!adjustConfirming ? (
              <>
                <label style={{ fontSize: 12, color: T.textSub }}>Quantity change (+/-)</label>
                <input
                  type="number"
                  value={adjustDelta}
                  onChange={(e) => setAdjustDelta(e.target.value)}
                  placeholder="e.g. -3 or 10"
                  style={{ ...input, width: "100%", marginTop: 4, marginBottom: 10, boxSizing: "border-box" }}
                />
                <label style={{ fontSize: 12, color: T.textSub }}>Reason</label>
                <select value={adjustType} onChange={(e) => setAdjustType(e.target.value)} style={{ ...input, width: "100%", marginTop: 4, marginBottom: 10, boxSizing: "border-box" }}>
                  {TXN_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
                <label style={{ fontSize: 12, color: T.textSub }}>Notes (optional)</label>
                <textarea
                  value={adjustNotes}
                  onChange={(e) => setAdjustNotes(e.target.value)}
                  rows={2}
                  style={{ ...input, width: "100%", marginTop: 4, marginBottom: 14, boxSizing: "border-box", resize: "vertical" }}
                />
                {error && <div style={{ color: T.red, marginBottom: 10, fontSize: 13 }}>{error}</div>}
                <div style={{ display: "flex", gap: 8 }}>
                  <GhostButton style={{ flex: 1 }} onClick={() => setAdjustRow(null)}>Cancel</GhostButton>
                  <PrimaryButton
                    style={{ flex: 1 }}
                    disabled={!parseInt(adjustDelta, 10)}
                    onClick={() => { setError(""); setAdjustConfirming(true); }}
                  >
                    Continue
                  </PrimaryButton>
                </div>
              </>
            ) : (
              <>
                <div style={{ fontSize: 14, marginBottom: 14 }}>
                  Confirm: <strong>{parseInt(adjustDelta, 10) > 0 ? `+${parseInt(adjustDelta, 10)}` : parseInt(adjustDelta, 10)}</strong> ({adjustType})
                  {" "}to {adjustRow.product_name}{adjustRow.variant_name ? ` — ${adjustRow.variant_name}` : ""}?
                  New on-hand will be <strong>{adjustRow.on_hand + parseInt(adjustDelta, 10)}</strong>.
                </div>
                {error && <div style={{ color: T.red, marginBottom: 10, fontSize: 13 }}>{error}</div>}
                <div style={{ display: "flex", gap: 8 }}>
                  <GhostButton style={{ flex: 1 }} onClick={() => setAdjustConfirming(false)} disabled={adjustBusy}>Back</GhostButton>
                  <PrimaryButton style={{ flex: 1 }} onClick={submitAdjust} disabled={adjustBusy}>
                    {adjustBusy ? "Saving…" : "Confirm Adjustment"}
                  </PrimaryButton>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
